import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import StatusBadge from '@/components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Plus, Filter, FileSpreadsheet, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_FILTERS = ['All', 'Draft', 'Submitted', 'CICForwarded', 'AHForwarded', 'Reviewed'];

export default function ReportsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState(null);
  const [centreFilter, setCentreFilter] = useState(null);
  const [centres, setCentres] = useState([]);
  const [bulkExportLoading, setBulkExportLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchFaculty, setSearchFaculty] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const isSuperOrPrincipal = ['SuperAdmin', 'CEO', 'PrincipalSSV'].includes(user?.role);
  const showCentreFilter = ['SuperAdmin', 'CEO', 'PrincipalSSV', 'AcademicHead'].includes(user?.role);
  const ROLE_OPTIONS = ['All', 'Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'];
  const ROLE_LABELS = { Faculty: 'Faculty', CIC: 'CIC', AcademicHead: 'Head', PrincipalSSV: 'Principal' };

  useEffect(() => {
    if (showCentreFilter) {
      api.getCentres().then(({ data }) => setCentres(data)).catch(() => {});
    }
  }, [showCentreFilter]);

  const handleBulkExport = async () => {
    setBulkExportLoading(true);
    try {
      const status = filter === 'All' ? null : filter;
      const res = await api.exportReportsBulkExcel(status);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `HIMT_Reports_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setBulkExportLoading(false);
    }
  };

  useEffect(() => {
    async function load() {
      try {
        const status = filter === 'All' ? null : filter;
        const { data } = await api.getReports(status, roleFilter === 'All' ? null : roleFilter, centreFilter && centreFilter !== 'All' ? centreFilter : null, fromDate || null, toDate || null);
        setReports(data);
      } catch (err) {
        console.error('Failed to load reports:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [filter, roleFilter, centreFilter, fromDate, toDate]);

  const filteredReports = searchFaculty.trim()
    ? reports.filter((r) => (r.faculty_name || '').toLowerCase().includes(searchFaculty.trim().toLowerCase()))
    : reports;

  return (
    <div data-testid="reports-list-page" className="space-y-4 sm:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Reports</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Weekly Reports
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="bulk-export-btn"
            onClick={handleBulkExport}
            disabled={bulkExportLoading}
            className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md font-medium px-3 sm:px-4 py-2 sm:py-2.5 text-sm transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">{bulkExportLoading ? 'Exporting...' : 'Export Excel'}</span>
            <span className="sm:hidden">{bulkExportLoading ? '...' : 'Excel'}</span>
          </button>
          {['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'].includes(user?.role) && (
          <button
            data-testid="new-report-btn"
            onClick={() => navigate('/reports/new')}
            className="flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-md font-medium px-5 py-2.5 text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Report
          </button>
          )}
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            data-testid={`filter-${s.toLowerCase()}`}
            onClick={() => { setFilter(s); setLoading(true); }}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              filter === s
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {s === 'CICForwarded' ? 'CIC Fwd' : s === 'AHForwarded' ? 'Head Fwd' : s}
          </button>
        ))}
      </div>

      {/* Role Filters — SuperAdmin/Principal */}
      {isSuperOrPrincipal && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">By Role:</span>
          {ROLE_OPTIONS.map((r) => (
            <button
              key={r}
              data-testid={`role-filter-${r.toLowerCase()}`}
              onClick={() => { setRoleFilter(r === 'All' ? null : r); setLoading(true); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                (roleFilter === r || (r === 'All' && !roleFilter))
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              {r === 'All' ? 'All Roles' : ROLE_LABELS[r] || r}
            </button>
          ))}
        </div>
      )}

      {/* Centre Filter — Principal/CEO/SuperAdmin/AcademicHead */}
      {showCentreFilter && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">By Centre:</span>
          {['All', ...centres].map((c) => (
            <button
              key={c}
              data-testid={`reports-centre-filter-${c.toLowerCase()}`}
              onClick={() => { setCentreFilter(c === 'All' ? null : c); setLoading(true); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                (centreFilter === c || (c === 'All' && !centreFilter))
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {c === 'All' ? 'All Centres' : c}
            </button>
          ))}
        </div>
      )}

      {/* Date range + Faculty search */}
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">From</label>
          <input
            data-testid="reports-from-date"
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setLoading(true); }}
            className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">To</label>
          <input
            data-testid="reports-to-date"
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setLoading(true); }}
            className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
          />
        </div>
        {(fromDate || toDate) && (
          <button
            data-testid="reports-clear-dates"
            onClick={() => { setFromDate(''); setToDate(''); setLoading(true); }}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1.5"
          >
            Clear dates
          </button>
        )}
        {user?.role !== 'Faculty' && (
          <div className="flex flex-col w-full sm:w-72 lg:w-80">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Search Faculty</label>
            <input
              data-testid="reports-search-faculty"
              type="text"
              placeholder="Type name…"
              value={searchFaculty}
              onChange={(e) => setSearchFaculty(e.target.value)}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
            />
          </div>
        )}
        <div className="ml-auto text-xs text-slate-500 self-center">
          {filteredReports.length} of {reports.length} report{reports.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Reports Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">Loading...</div>
          ) : filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">{user?.role === 'Faculty' ? 'Date' : 'Period'}</th>
                    {user?.role !== 'Faculty' && (
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Faculty</th>
                    )}
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Status</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Sections</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Submitted</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((r) => {
                    const rowCount = (r.regular_duties?.length || 0) + (r.responsibilities?.length || 0) + (r.course_marketings?.length || 0);
                    const isExpanded = expandedIds.has(r._id);
                    const colCount = user?.role !== 'Faculty' ? 6 : 5;
                    return (
                    <React.Fragment key={r._id}>
                      <tr
                        data-testid={`report-item-${r._id}`}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-sm font-medium text-slate-800">
                          {r.report_date ? r.report_date : `${r.week_from} — ${r.week_to}`}
                        </td>
                        {user?.role !== 'Faculty' && (
                          <td className="px-5 py-3.5 text-sm text-slate-600">{r.faculty_name}</td>
                        )}
                        <td className="px-5 py-3.5"><StatusBadge status={r.report_status} /></td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">
                          <button
                            data-testid={`toggle-expand-${r._id}`}
                            onClick={() => toggleExpand(r._id)}
                            disabled={rowCount === 0}
                            className={`flex items-center gap-1 font-medium transition-colors ${rowCount > 0 ? 'text-slate-700 hover:text-blue-700 cursor-pointer' : 'text-slate-400 cursor-default'}`}
                          >
                            {rowCount} rows
                            {rowCount > 0 && (isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">
                          {r.submitted_date ? new Date(r.submitted_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-3.5">
                          <button
                            data-testid={`view-report-${r._id}`}
                            onClick={() => navigate(`/reports/${r._id}`)}
                            className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr data-testid={`expanded-detail-${r._id}`} className="bg-slate-50/70 border-b border-slate-200">
                          <td colSpan={colCount} className="px-5 py-4">
                            <ReportExpandedDetail report={r} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FileText className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">No reports found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Inline expandable detail — shows the full activity breakdown (Academics, Responsibilities,
// Initiatives) for a report row, scrollable if content is tall instead of growing the page.
function ReportExpandedDetail({ report }) {
  const duties = report.regular_duties || [];
  const resps = report.responsibilities || [];
  const mktgs = report.course_marketings || [];
  if (!duties.length && !resps.length && !mktgs.length) {
    return <p className="text-sm text-slate-400 italic py-1">No activity entries in this report.</p>;
  }
  const Section = ({ title, color, headers, rows, cells }) => (
    <div>
      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${color.text}`}>{title}</p>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className={color.bg}>
            {headers.map(h => (
              <th key={h} className={`text-left px-2 py-1.5 font-bold uppercase text-[9px] border border-slate-200 ${color.text}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 bg-white">
              {cells(row).map((c, ci) => <td key={ci} className="px-2 py-1.5 border border-slate-100 text-slate-700">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div data-testid="expanded-detail-content" className="max-h-[450px] overflow-y-auto space-y-4 pr-1">
      {duties.length > 0 && (
        <Section
          title="Academics / Regular Duties" color={{ text: 'text-blue-700', bg: 'bg-blue-50' }}
          headers={['Date', 'Topics', 'Hours', 'Days Absent', 'Remarks', 'Status']} rows={duties}
          cells={d => [d.date || '—', d.topics || '—', <span className="font-semibold">{d.total_hours}h</span>, d.days_absent || 0, d.remarks || '—', <StatusBadge status={d.status} />]}
        />
      )}
      {resps.length > 0 && (
        <Section
          title="Responsibilities" color={{ text: 'text-slate-700', bg: 'bg-slate-100' }}
          headers={['Group', 'Date', 'Topics', 'Description', 'Hours', 'Status']} rows={resps}
          cells={r => [r.group_name || '—', r.date || '—', r.topics || '—', r.description || '—', <span className="font-semibold">{r.time_spent_hrs}h</span>, <StatusBadge status={r.status} />]}
        />
      )}
      {mktgs.length > 0 && (
        <Section
          title="Initiatives" color={{ text: 'text-emerald-700', bg: 'bg-emerald-50' }}
          headers={['Date', 'Topics', 'Description', 'Hours', 'Status']} rows={mktgs}
          cells={m => [m.date || '—', m.topics || '—', m.description || '—', <span className="font-semibold">{m.hours}h</span>, <StatusBadge status={m.status} />]}
        />
      )}
    </div>
  );
}
